/**
 * @file Multi/Custom select component.
 */

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Select from 'react-select';
import Creatable from 'react-select/lib/Creatable';
import Async from 'react-select/lib/Async';

import Label from './Label';

export const REACT_SELECT_ACTIONS = {
  INPUT_CHANGE: 'input-change',
  INPUT_BLUR: 'input-blur',
};

const propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  labelNote: PropTypes.string,
  labelTip: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  labelSrOnly: PropTypes.bool,
  labelType: PropTypes.string,
  className: PropTypes.string,
  selectClassName: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.string,
      }),
    ]),
  ),
  loadOptions: PropTypes.func,
  inline: PropTypes.bool,
  creatable: PropTypes.bool,
  async: PropTypes.bool,
  required: PropTypes.bool,
};

const MultiSelect = ({
  id,
  label,
  labelNote,
  labelTip,
  labelSrOnly,
  labelType,
  className,
  selectClassName,
  options,
  inline,
  creatable,
  loadOptions,
  required,
  ...props
}) => {
  const classes = classNames({
    'form-group': true,
    'form-group-inline': inline,
    [className]: !!className,
  });

  const selectClasses = classNames({
    'multi-select-control': true,
    [selectClassName]: !!selectClassName,
  });

  const selectProps = {
    inputId: id,
    options,
    loadOptions,
    className: selectClasses,
    classNamePrefix: 'multi-select',
    'aria-label': label,
    'aria-labelledby': `label-for-${id}`,
    ...props,
  };

  const ComponentActual = () => {
    if (loadOptions) {
      return <Async {...selectProps} />;
    }

    if (creatable) {
      return <Creatable {...selectProps} />;
    }

    return <Select {...selectProps} />;
  };

  return (
    <div className={classes}>
      {label && (
        <Label
          id={id}
          label={label}
          labelNote={labelNote}
          labelTip={labelTip}
          labelSrOnly={labelSrOnly}
          labelType={labelType}
          required={required}
        />
      )}

      <ComponentActual />
    </div>
  );
};

MultiSelect.propTypes = propTypes;

export default MultiSelect;
